import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import type React from 'react';
import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useSgCurrencyPairPickerContext } from './context.tsx';

export const CurrencyPairList = () => {
  const {
    state: { filteredCurrencyPairs, editedValue, selectedCurrencyPair },
    comboBox: { getMenuProps, getItemProps, highlightedIndex, isOpen },
    inputRef,
  } = useSgCurrencyPairPickerContext();

  // References for @floating-ui/react
  const floatingRef = useRef<HTMLUListElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    elements: {
      reference: inputRef,
    },
    middleware: [
      offset(4), // 4px spacing between input and menu
      flip(), // Automatic flip if no space
      shift({ padding: 8 }), // Shift to avoid window edges
      size({
        apply({ rects, elements, availableHeight }) {
          // Make menu have same width as input
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(200, availableHeight - 16)}px`, // Max height with 16px margin
            overflowY: 'auto', // Vertical scroll if needed
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Interactions for floating-ui (optional, to close with ESC)
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const highlightMatchedChars = useCallback((currencyPair: string, inputValue: string) => {
    if (!inputValue) {
      return <span>{currencyPair}</span>;
    }

    const normalizedInput = inputValue.replace(' ', '/');
    const result: React.ReactElement[] = [];
    let inputIndex = 0;
    let charIndex = 0;

    for (let currencyPairIndex = 0; currencyPairIndex < currencyPair.length; currencyPairIndex++) {
      const char = currencyPair.charAt(currencyPairIndex);

      const isLetter = /[A-Z]/i.test(char);

      if (inputIndex < normalizedInput.length && char === normalizedInput[inputIndex]) {
        // Matching character - bold
        if (isLetter) {
          result.push(<strong key={charIndex}>{char}</strong>);
        } else {
          result.push(<span key={charIndex}>{char}</span>);
        }

        inputIndex++;
      } else {
        // Normal character
        result.push(<span key={charIndex}>{char}</span>);
      }

      charIndex++;
    }

    return <>{result}</>;
  }, []);

  // All hooks called above — early return is safe here.
  // When closed: the <ul> is not mounted → autoUpdate never subscribes to scroll events.
  if (!isOpen) {
    // call getMenuProps to satisfy downshift's requirement that it be called at least once, even when the menu is closed
    getMenuProps({}, { suppressRefError: true });
    return null;
  }

  return createPortal(
    <ul
      {...getMenuProps({
        ref: (node: HTMLUListElement | null) => {
          // Combine downshift and floating-ui refs
          floatingRef.current = node;
          refs.setFloating(node);
        },
      })}
      {...getFloatingProps()}
      className="dropdown-menu p-0 z-10 bg-white show"
      style={{
        ...floatingStyles,
        zIndex: 9999, // Ensure menu is above everything
        // Scroll styles are already applied by floating-ui
      }}
    >
      {filteredCurrencyPairs.length > 0 ? (
        filteredCurrencyPairs.map((currencyPair, currencyPairIndex) => (
          <li
            key={currencyPair}
            {...getItemProps({
              index: currencyPairIndex,
              item: currencyPair,
              style: {
                backgroundColor: highlightedIndex === currencyPairIndex ? 'lightgray' : 'white',
                fontWeight: selectedCurrencyPair === currencyPair ? 'bold' : 'normal',
              },
            })}
            className="px-2 py-1 text-center"
          >
            {highlightMatchedChars(currencyPair, editedValue)}
          </li>
        ))
      ) : (
        <li className="px-2 py-1 text-center text-muted">aucun résultat</li>
      )}
    </ul>,
    document.body,
  );
};
